import {
    Box,
    Combobox,
    ComboboxInput,
    ComboboxList,
    ComboboxOption,
    ComboboxPopover,
    FlexBox,
    FormField,
    FormLabel,
    Link,
    Spinner,
    ToggleSwitch,
    Typography,
} from '@vp/swan';
import styled from 'styled-components';
import { ANY_DESIGNER_CALENDAR_ID } from '@99designs/feature-design-services-brief';
import { __ } from '@99designs/i18n';
import type { PreviousDesigner } from '@99designs/feature-design-services-brief';

const SignInLink = styled(Link)`
    &.swan-link.swan-link-skin-cta:hover {
        text-decoration: underline;
    }
`;

export interface ChatDesignerSelectorProps {
    id: string;
    isSignedIn: boolean;
    onSignIn: () => void;
    matchWithPreviousDesigner: boolean;
    onToggleChange: (activated: boolean) => void;
    designers: PreviousDesigner[];
    isLoadingDesigners: boolean;
    hasDesignersError: boolean;
    calendarId: number;
    setCalendarId: (calendarId: number) => void;
}

/**
 * Designer-matching toggle + picker for the chat brief's DesignLive scheduling step.
 *
 * A dedicated component rather than a shared one with the multi-step brief's
 * DesignerSelector: this step's own title already covers what that component's
 * "Designer selection" heading would say, and its field labels are normal weight with
 * more spacing above their input, not the bold/tighter labels the multi-step brief form
 * uses for its own fields. Chat replies also never lock a settled answer for editing the
 * way the multi-step brief's `immutable` appointments do, so there is no disabled state
 * to thread through here either.
 */
export function ChatDesignerSelector({
    id,
    isSignedIn,
    onSignIn,
    matchWithPreviousDesigner,
    onToggleChange,
    designers,
    isLoadingDesigners,
    hasDesignersError,
    calendarId,
    setCalendarId,
}: ChatDesignerSelectorProps) {
    const toggleLabel = __("Match me with a designer I've worked with before");

    return (
        <Box data-testid={`${id}-section`}>
            <FormField>
                <FlexBox alignItems="center" justifyContent="space-between" gap="between-actions">
                    <FormLabel fontSkin="body-standard" htmlFor={id} mb={0}>
                        {toggleLabel}
                    </FormLabel>
                    <ToggleSwitch
                        id={id}
                        hideText
                        standardMode
                        activated={matchWithPreviousDesigner}
                        aria-label={toggleLabel}
                        onRequestActivatedChange={(activated, e) => {
                            e.preventDefault();
                            onToggleChange(activated);
                        }}
                    />
                </FlexBox>
            </FormField>

            {matchWithPreviousDesigner && (
                <ChatDesignerMatchContent
                    id={id}
                    isSignedIn={isSignedIn}
                    onSignIn={onSignIn}
                    designers={designers}
                    isLoadingDesigners={isLoadingDesigners}
                    hasDesignersError={hasDesignersError}
                    calendarId={calendarId}
                    setCalendarId={setCalendarId}
                />
            )}
        </Box>
    );
}

interface ChatDesignerMatchContentProps {
    id: string;
    isSignedIn: boolean;
    onSignIn: () => void;
    designers: PreviousDesigner[];
    isLoadingDesigners: boolean;
    hasDesignersError: boolean;
    calendarId: number;
    setCalendarId: (calendarId: number) => void;
}

// Renders the state below the toggle once designer matching is enabled.
// The states are mutually exclusive and evaluated top to bottom.
function ChatDesignerMatchContent({
    id,
    isSignedIn,
    onSignIn,
    designers,
    isLoadingDesigners,
    hasDesignersError,
    calendarId,
    setCalendarId,
}: ChatDesignerMatchContentProps) {
    if (!isSignedIn) {
        return (
            <Box mt={3}>
                <SignInLink
                    skin="cta"
                    render={({ children, ...props }) => (
                        <button type="button" {...props} onClick={onSignIn}>
                            {children}
                        </button>
                    )}
                >
                    {__('Sign in to select from your previous designers', {
                        _context:
                            'Link prompting a signed-out customer to sign in so they can pick a designer they have worked with before',
                    })}
                </SignInLink>
            </Box>
        );
    }

    if (isLoadingDesigners) {
        return (
            <Box mt={4} textAlign="center" data-testid={`${id}-loading`}>
                <Spinner accessibleText={__('Loading your previous designers...')} />
            </Box>
        );
    }

    if (designers.length > 0) {
        return (
            <Box mt={6}>
                <FormField>
                    <FormLabel
                        id={`${id}-dropdown-label`}
                        fontSkin="body-standard"
                        marginBottom={'4'}
                    >
                        {__('Choose from your past designers', {
                            _context:
                                'Label for a dropdown to pick which previously-worked-with designer to book a DesignLive session with',
                        })}
                    </FormLabel>
                    <Combobox
                        selectedKey={String(calendarId)}
                        onSelectionChange={(key) => {
                            if (key !== null) setCalendarId(Number(key));
                        }}
                        menuTrigger="focus"
                        aria-labelledby={`${id}-dropdown-label`}
                        marginTop={'2'}
                    >
                        <ComboboxInput
                            data-testid={`${id}-dropdown`}
                            placeholder={__('Select a designer')}
                            aria-labelledby={`${id}-dropdown-label`}
                        />
                        <ComboboxPopover>
                            <ComboboxList data-testid={`${id}-dropdown-list`}>
                                {designers.map((designer) => (
                                    <ComboboxOption
                                        key={designer.acuityCalendarId}
                                        textValue={designer.designerName}
                                    >
                                        {designer.designerName}
                                    </ComboboxOption>
                                ))}
                            </ComboboxList>
                        </ComboboxPopover>
                    </Combobox>
                </FormField>
                <Typography fontSkin="body-small" textColor="subtle" mt={2}>
                    {__("Selecting a designer will only show times when they're available", {
                        _context:
                            'Helper text explaining that picking a designer filters the available appointment times',
                    })}
                </Typography>
            </Box>
        );
    }

    if (hasDesignersError) {
        // Only claim an any-designer fallback when no specific designer is still selected.
        if (calendarId !== ANY_DESIGNER_CALENDAR_ID) {
            return null;
        }
        return (
            <Typography fontSkin="body-small" textColor="subtle" mt={3}>
                {__(
                    "We couldn't load your previous designers. We'll match you with a designer that suits your selected time.",
                    {
                        _context:
                            'Helper text shown when loading the customer\u2019s previous designers failed',
                    }
                )}
            </Typography>
        );
    }

    return (
        <Typography fontSkin="body-small" textColor="subtle" mt={3}>
            {__(
                "You haven't worked with a designer before, so we'll match you with a designer that suits your selected time.",
                {
                    _context:
                        'Helper text shown when a customer has opted in to designer matching but has no previous designers',
                }
            )}
        </Typography>
    );
}
